/** Generic, minimally-styled data table for tabular resources. */

import React from "react";

export interface DataTableColumn<T> {
  header: string;
  accessor: (row: T) => React.ReactNode;
  align?: "left" | "right";
}

interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  emptyMessage?: string;
}

export default function DataTable<T>({
  columns,
  rows,
  rowKey,
  emptyMessage = "No data yet.",
}: DataTableProps<T>): React.ReactElement {
  if (rows.length === 0) {
    return (
      <div className="panel flex items-center justify-center py-12 text-sm text-mist-400">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className="panel overflow-hidden">
      <table className="w-full text-left text-sm">
        <thead>
          <tr className="border-b border-ink-700 bg-ink-800/50">
            {columns.map((column) => (
              <th
                key={column.header}
                className={`px-4 py-3 text-xs font-medium uppercase tracking-wide text-mist-400 ${
                  column.align === "right" ? "text-right" : "text-left"
                }`}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={rowKey(row)} className="border-b border-ink-800 last:border-0">
              {columns.map((column) => (
                <td
                  key={column.header}
                  className={`px-4 py-3 text-mist-200 ${
                    column.align === "right" ? "text-right" : "text-left"
                  }`}
                >
                  {column.accessor(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
